/**
 * Morse Code Tool
 *
 * Convert text to/from Morse code using a lookup table.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

const CHAR_TO_MORSE: Record<string, string> = {
  'A': '.-',     'B': '-...',   'C': '-.-.',   'D': '-..',
  'E': '.',      'F': '..-.',   'G': '--.',    'H': '....',
  'I': '..',     'J': '.---',   'K': '-.-',    'L': '.-..',
  'M': '--',     'N': '-.',     'O': '---',    'P': '.--.',
  'Q': '--.-',   'R': '.-.',    'S': '...',    'T': '-',
  'U': '..-',    'V': '...-',   'W': '.--',    'X': '-..-',
  'Y': '-.--',   'Z': '--..',
  '0': '-----',  '1': '.----',  '2': '..---',  '3': '...--',
  '4': '....-',  '5': '.....',  '6': '-....',  '7': '--...',
  '8': '---..',  '9': '----.',
  '.': '.-.-.-', ',': '--..--', '?': '..--..', "'": '.----.',
  '!': '-.-.--', '/': '-..-.',  '(': '-.--.',  ')': '-.--.-',
  '&': '.-...',  ':': '---...', ';': '-.-.-.', '=': '-...-',
  '+': '.-.-.',  '-': '-....-', '_': '..--.-', '"': '.-..-.',
  '$': '...-..-','@': '.--.-.', ' ': '/',
};

const MORSE_TO_CHAR: Record<string, string> = {};
for (const [char, morse] of Object.entries(CHAR_TO_MORSE)) {
  if (char !== ' ') {
    MORSE_TO_CHAR[morse] = char;
  }
}

export const MorseCodeTool: ITransformerTool = {
  id: 'morse-code',
  name: 'Morse Code',
  description: 'Convert text to/from Morse code',
  version: '1.0.0',
  type: 'transformer',
  category: 'Encoders & Decoders',
  keywords: ['morse', 'code', 'encode', 'decode', 'telegraph', 'dots', 'dashes'],

  seo: {
    slug: 'morse-code-converter',
    pageTitle: 'Morse Code Converter - Free Online Tool | SWE DevTools',
    metaDescription: 'Convert text to Morse code and back. Supports letters, numbers, and punctuation. Fast, private, runs entirely in your browser.',
    keywords: ['morse code converter', 'text to morse', 'morse to text', 'morse code translator', 'morse encoder'],
    relatedTools: ['rot13', 'base64-encode', 'hex-encode'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'mode',
      type: 'select',
      label: 'Mode',
      defaultValue: 'encode',
      options: ['encode', 'decode'],
      description: 'Encode text to Morse or decode Morse to text',
    },
    {
      name: 'letterSeparator',
      type: 'select',
      label: 'Letter Separator',
      defaultValue: ' ',
      options: [' ', '  ', ' | '],
      description: 'Separator between Morse code letters',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const mode = String(config.mode ?? 'encode');
    const separator = String(config.letterSeparator ?? ' ');
    const text = String(input ?? '');
    if (!text.trim()) return '';

    try {
      if (mode === 'encode') {
        return encodeToMorse(text, separator);
      } else {
        return decodeFromMorse(text, separator);
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to convert Morse code';
      throw ToolError.execution(message);
    }
  },
};

function encodeToMorse(text: string, separator: string): string {
  const words = text.toUpperCase().split(/\s+/);
  const morseWords = words.map((word) => {
    const letters = word.split('').map((char) => {
      const morse = CHAR_TO_MORSE[char];
      if (!morse) return char; // Pass through unknown characters
      return morse;
    });
    return letters.join(separator);
  });
  return morseWords.join(' / ');
}

function decodeFromMorse(text: string, separator: string): string {
  // Normalize word separators
  const words = text.split(/\s*\/\s*/);
  const decoded = words.map((word) => {
    // Split by the configured separator (try configured first, fallback to space)
    const letters = separator.trim()
      ? word.split(separator)
      : word.split(/\s+/);
    return letters
      .map((morse) => morse.trim())
      .filter((morse) => morse.length > 0)
      .map((morse) => MORSE_TO_CHAR[morse] ?? '?')
      .join('');
  });
  return decoded.join(' ');
}
